import SimpleLineGraph from 'components/SimpleLineGraph/SimpleLineGraph';
import Highcharts, {
    SeriesLineOptions,
    Options,
    AxisSetExtremesEventObject,
} from 'highcharts/highstock';
import { useEffect, useMemo } from 'react';
import DarkTheme from 'highcharts/themes/high-contrast-dark';
import LightTheme from 'highcharts/themes/high-contrast-light';
import { useUi } from 'hooks/useUi';
import { endOfMonth } from 'date-fns';

interface EfficiencyGraphProps {
    series: SeriesLineOptions[];
    setMinDate: (d: Date) => void;
    deliverySizeTarget: number;
    isSupplierPage?: boolean;
}

export const EfficiencyGraph = ({
    deliverySizeTarget,
    series,
    setMinDate,
    isSupplierPage,
}: EfficiencyGraphProps) => {
    const { ui } = useUi();

    const options: Options = useMemo(
        () => ({
            chart: {
                height: isSupplierPage ? '220px' : '280px',
            },
            rangeSelector: {
                selected: 6,
                buttonSpacing: 5,
                buttonTheme: {
                    width: 80,
                },
                enabled: true,
                buttons: [
                    {
                        type: 'month',
                        count: 1,
                        text: '1 month',
                    },
                    {
                        type: 'month',
                        count: 3,
                        text: '3 months',
                    },
                    {
                        type: 'month',
                        count: 6,
                        text: '6 months',
                    },
                    {
                        type: 'ytd',
                        text: 'Year to date',
                    },
                    {
                        type: 'year',
                        count: 1,
                        text: '1 year',
                    },
                    {
                        type: 'year',
                        count: 2,
                        text: '2 years',
                    },
                    {
                        type: 'all',
                        text: 'Show All',
                    },
                ],
            },
            xAxis: {
                minRange: 30 * 24 * 60 * 60 * 1000,
                max: endOfMonth(new Date()).valueOf(),
                type: 'datetime',
                labels: {
                    // eslint-disable-next-line quotes
                    format: "{value:%b '%y}",
                },
                events: {
                    afterSetExtremes: (e: AxisSetExtremesEventObject) =>
                        setMinDate(new Date(e.min)),
                },
            },
            plotOptions: {
                series: {
                    marker: {
                        radius: 5,
                        enabled: true,
                    },
                    dataGrouping: {
                        anchor: 'start',
                    },
                },
            },
            yAxis: [
                {
                    id: 'delivery_count',
                    allowDecimals: false,
                    opposite: true,
                    title: {
                        text: 'Delivery Count',
                    },
                    labels: {
                        format: '{value}',
                    },
                },
                {
                    id: 'delivery_size',
                    min: 0,
                    max: 100,
                    plotLines: [
                        {
                            value: deliverySizeTarget,
                            color: '#111',
                            dashStyle: 'ShortDash',
                            width: 1,
                            label: {
                                text: 'Delivery Size Target',
                            },
                        },
                    ],
                    opposite: false,
                    endOnTick: false,
                    title: {
                        text: 'Delivery Size',
                    },
                    labels: {
                        format: '{value} %',
                    },
                },
            ],
            tooltip: {
                shared: true,
            },
        }),
        [deliverySizeTarget, isSupplierPage, setMinDate]
    );

    // Set graph theme based on user's color theme.
    useEffect(() => {
        if (ui.theme === 'dark') DarkTheme(Highcharts);
        else LightTheme(Highcharts);
    }, [ui.theme]);

    return (
        <div data-testid="EfficiencyGraph" style={{ height: '100%' }}>
            <SimpleLineGraph
                series={series}
                highcharts={Highcharts}
                options={options}
                data-testid="EfficiencyGraph"
            />
        </div>
    );
};
